import { TColor, TSize } from '@src/models/common';
import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { FiltersController } from '../FiltersController';
import { TDateRange } from '../models';
import { getOptionDopText } from './utils';
import { TNoOfLinesAdaptive } from '@src/models/adaptive';
import { IWithStylesProps } from '@src/models/props';
import { getStringDateRangeValue } from '../utils';
import { FILTER_CONTROLLER_EVENTS, useSyncFilterValue } from '../useSyncValue';
import { TTagVariant } from '@src/atoms/Tag';
import { FiltersBadgeSingleTagHoc } from './FiltersBadgeSingleTag/FiltersBadgeSingleTag';

interface IFiltersBadgeProps extends IWithStylesProps {
  /** Контроллер фильтра */
  controller: FiltersController;

  /** Имя фильтра, другими словами его уникальный key */
  filterName: string;

  /** @deprecated - теперь все фильтры стандартные на всех страницах */
  view?: TColor;

  /** @deprecated - теперь все фильтры стандартные на всех страницах */
  variant?: TTagVariant;

  /** @deprecated - теперь все фильтры стандартные на всех страницах */
  textColor?: TColor;

  /** Ссылка для кнопки закрытия */
  href?: string;

  /** @deprecated - теперь все фильтры стандартные на всех страницах */
  size?: TSize;

  /** @deprecated логика закрытия бейджа перенесена внутрь бейджа */
  onClose?: (e: MouseEvent<SVGSVGElement>) => void;

  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;

  noOfLines?: TNoOfLinesAdaptive;

  /** Каждый примененный фильтр отображается в своем теге */
  doNotGroup?: boolean;

  /** Отображение года в теге */
  showYear?: boolean;
}

/**
 * Умный компонент адаптированный под работу с фильтрами.
 * В случае фильтра с типом = `опции` будет догружать опции если они еще не проинициализированы
 */
export const FiltersBadge: FC<IFiltersBadgeProps> = ({
  filterName,
  controller,
  href,
  noOfLines,
  className,
  style,
  onClick,
  doNotGroup,
  showYear,
}) => {
  const [dopText, setDopText] = useState<string[] | undefined>(undefined);

  const schemeList = useSyncFilterValue({ controller, event: FILTER_CONTROLLER_EVENTS.SCHEME });
  const valueList = useSyncFilterValue({ controller, event: FILTER_CONTROLLER_EVENTS.VALUE_LIST });

  const filterScheme = schemeList.get(filterName);
  const filterValue = valueList.get(filterName)?.defaultValue;

  useEffect(() => {
    if (!filterScheme) return;

    if (filterScheme.getBadgeText !== undefined) {
      (async () => {
        const result = await filterScheme.getBadgeText?.({
          scheme: filterScheme as never,
          value: filterValue,
          controller,
        });
        setDopText(result);
      })();

      return;
    }

    if (filterScheme.type === 'string') {
      setDopText([filterValue as string]);
    }

    if (filterScheme.type === 'dateRange') {
      const dataText = getStringDateRangeValue(filterValue as TDateRange, showYear);
      setDopText([dataText]);
      return;
    }

    if (filterScheme.type === 'options') {
      const getDopText = async () => {
        const newDopText = await getOptionDopText({ controller, filterScheme, filterValue, doNotGroup });
        if (Array.isArray(newDopText)) {
          setDopText(newDopText);
        } else {
          setDopText([newDopText]);
        }
      };

      getDopText();
    }
  }, [controller, filterScheme, filterValue, doNotGroup]);

  const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
    const nodeName = (e.target as unknown as { nodeName: string }).nodeName;
    if (nodeName === 'path' || nodeName === 'svg') {
      return;
    }

    onClick?.(e);
  };

  if (!filterScheme || !filterValue) return null;

  if (dopText) {
    return (
      <>
        {dopText.map((el, i) => {
          return (
            <FiltersBadgeSingleTagHoc
              key={filterName + i}
              index={i}
              classes={className}
              style={style}
              handleClick={handleClick}
              title={filterScheme.title}
              href={href}
              noOfLines={noOfLines}
              text={el}
              controller={controller}
              filterName={filterName}
              doNotGroup={doNotGroup}
              singlePredicate={dopText.length === 1}
            />
          );
        })}
      </>
    );
  }

  return (
    <FiltersBadgeSingleTagHoc
      classes={className}
      style={style}
      handleClick={handleClick}
      title={filterScheme.title}
      href={href}
      noOfLines={noOfLines}
      controller={controller}
    />
  );
};
